import { create } from 'zustand';

import { toPlayerMessage } from '@/shared/api';
import { isFresh, singleFlight } from '@/shared/lib';

import {
  answerDerby,
  fetchCurrentDerby,
  markDerbyNudgeSeen,
  openNextDerbyQuestion,
  startDerby,
} from '../api/derby.api';

import type { DerbyOption, DerbyState } from './types';

interface DerbyStoreState {
  /** `null` — открытого Дерби нет; до первой загрузки тоже `null`. */
  derby: DerbyState | null;
  isLoading: boolean;
  isAnswering: boolean;
  error: string | null;
  /**
   * Когда загрузили. Отдельным полем, потому что `null` в `derby` —
   * валидный ответ «Дерби сейчас нет», и по нему нельзя понять,
   * спрашивали ли уже сервер.
   */
  loadedAt: number | null;

  /**
   * `start` — открыть Дерби, а не просто прочитать: сервер запускает
   * таймер первого вопроса. Зовёт только экран Дерби.
   */
  load: (options?: { force?: boolean; start?: boolean }) => Promise<void>;
  /**
   * Открыть следующий вопрос — с этого момента идёт его таймер.
   * Зовёт кнопка «Следующий»; пока игрок читает факт, время не идёт.
   */
  openNext: () => Promise<void>;
  answer: (questionId: number, option: DerbyOption) => Promise<void>;
  /**
   * Игрок закрыл приглашение «Дерби началось». Панель гасим сразу,
   * отметку ставит сервер: сегодня приглашение больше не придёт —
   * ни после обновления, ни на другом устройстве, — а назавтра
   * вернётся, пока Дерби идёт и не пройдено.
   */
  dismissNudge: () => Promise<void>;
  reset: () => void;
}

export const useDerbyStore = create<DerbyStoreState>()((set, get) => ({
  derby: null,
  isLoading: false,
  isAnswering: false,
  error: null,
  loadedAt: null,

  // Дерби спрашивают и лента матчей, и сам квиз — запрос должен уйти один
  load: singleFlight(
    async (options?: { force?: boolean; start?: boolean }): Promise<void> => {
      // Старт свежесть не сокращает: он идемпотентен, но пропустить его
      // из-за недавнего чтения ленты значило бы не запустить таймер
      if (!options?.force && !options?.start && isFresh(get().loadedAt)) return;

      set({ isLoading: true, error: null });
      try {
        const derby = options?.start
          ? await startDerby()
          : await fetchCurrentDerby();
        set({ derby, loadedAt: Date.now() });
      } catch (error) {
        set({ error: toPlayerMessage(error) });
      } finally {
        set({ isLoading: false });
      }
    },
  ),

  /**
   * Открывает следующий вопрос и запускает ЕГО таймер.
   *
   * Отдельное действие, а не побочный эффект перехода на клиенте: время
   * отмеряет сервер, и клиент не имеет права решать, когда оно пошло.
   * Идемпотентно на сервере — повторное нажатие таймер не сбрасывает.
   */
  openNext: async () => {
    set({ error: null });
    try {
      set({ derby: await openNextDerbyQuestion(), loadedAt: Date.now() });
    } catch (error) {
      set({ error: toPlayerMessage(error) });
    }
  },

  /**
   * Ответ фиксируется на сервере навсегда (ГДД §6.2), поэтому оптимистично
   * ничего не рисуем: сначала ответ сервера, потом состояние. Иначе при
   * отказе пришлось бы откатывать уже показанную игроку правильность.
   */
  dismissNudge: async (): Promise<void> => {
    const current = get().derby;
    if (current) set({ derby: { ...current, shouldNudge: false } });
    try {
      await markDerbyNudgeSeen();
    } catch {
      // Игрок уже закрыл панель, повторять ему нечего. Не отметилось —
      // приглашение вернётся при следующем входе; ронять на этом экран
      // хуже, чем показать приглашение ещё раз
    }
  },

  answer: async (questionId, option) => {
    if (get().isAnswering) return;
    set({ isAnswering: true, error: null });
    try {
      const result = await answerDerby({ questionId, option });
      const derby = get().derby;
      if (!derby) return;

      // Ключи собираем условно: при `exactOptionalPropertyTypes` присвоить
      // необязательному полю `undefined` нельзя, а на последнем вопросе
      // `currentPosition`/`currentDeadline` должны исчезнуть, а не стать пустыми.
      const {
        currentPosition: _,
        currentDeadline: __,
        currentTimeLimitSeconds: _____,
        pointsAwarded: ___,
        pointsSoFar: ____,
        ...rest
      } = derby;
      const next: DerbyState = {
        ...rest,
        questions: derby.questions.map((q) =>
          q.id === questionId
            ? {
                ...q,
                chosenOption: option,
                isCorrect: result.isCorrect,
                ...(result.factText === undefined
                  ? {}
                  : { factText: result.factText }),
              }
            : q,
        ),
        correctCount: result.correctCount,
        // Ступень лесенки за текущий результат — её показывает экран
        // ответа рядом с вердиктом. Считать на клиенте нечем: лесенки тут нет
        ...(result.pointsSoFar === undefined
          ? {}
          : { pointsSoFar: result.pointsSoFar }),
        isFinished: result.isFinished,
        ...(result.nextPosition === undefined
          ? {}
          : { currentPosition: result.nextPosition }),
        // `currentDeadline` сюда НЕ возвращается — он отброшен
        // деструктуризацией выше, и это правильно: у следующего вопроса
        // таймер ещё не запущен, он начнётся по кнопке «Следующий»
        // (`POST /derby/next`). Вернуть прежнее значение значило бы
        // показывать на экране факта дедлайн только что отвеченного
        // вопроса — просроченный, и клиент побежал бы перечитывать
        // состояние вместо того, чтобы дать спокойно прочитать факт
        ...(result.pointsAwarded === undefined
          ? {}
          : { pointsAwarded: result.pointsAwarded }),
      };

      set({ derby: next });
    } catch (error) {
      set({ error: toPlayerMessage(error) });
    } finally {
      set({ isAnswering: false });
    }
  },

  reset: () => {
    set({ derby: null, error: null, loadedAt: null });
  },
}));
